import { Faqs } from '@seek/faqs';
import { useMelwaysLink } from '@seek/melways-react';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Box, TextLink } from 'braid-design-system';

import { useAppContext } from 'src/App/context/AppContext';
import { useTracking } from 'src/App/context/TrackingProvider';

import type { AdBudgetSelectionPageBaseEvent } from '../../eventTracking/createEvent';

import translations from './.vocab';

interface FaqProps {
  selectedBudgetType: string;
  trackingBaseEvent: AdBudgetSelectionPageBaseEvent;
}

const Faq = ({ trackingBaseEvent, selectedBudgetType }: FaqProps) => {
  const { t } = useTranslations(translations);
  const localize = useMelwaysLink();
  const { language } = useAppContext();
  const { trackLink } = useTracking();

  const handleAdPriceLookUpOnClick = () => {
    trackLink({
      eventName: 'seek_contract_lookup_exact_ad_prices_pressed',
      data: { ...trackingBaseEvent },
    });
  };

  const faqs = [
    {
      key: 'id_1',
      label: t('how does my ad budget...'),
      copy: t('an ad budget is prepaid...'),
    },
    {
      key: 'id_2',
      label: t('what are the different...'),
      copy: t('visit our...', {
        TextLink: (children) => (
          <TextLink
            href={localize({
              path: 'jobads',
              language: language as Language,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </TextLink>
        ),
      }),
    },
    {
      key: 'id_3',
      label: t('how long do i have...'),
      copy: [
        t('your advertising agreement...'),
        <br key="line_break_1" />,
        t('if you have an existing...'),
        <br key="line_break_2" />,
        t('if you do not have...'),
      ],
    },
    {
      key: 'id_4',
      label: t('what happens if my balance...'),
      copy: t('the amount left in...'),
    },
    {
      key: 'id_5',
      label: t('what happens if i use...'),
      copy:
        selectedBudgetType === 'PremiumBudget'
          ? t('you’ll enjoy your discount for 12 months...')
          : t("if you've used up your..."),
    },
    {
      key: 'id_6',
      label: t("what happens if i don't..."),
      copy: t('there are no refunds...'),
    },
    {
      key: 'id_7',
      label: t('how many ads can...'),
      copy: t('each ad budget gives...', {
        TextLink: (children) => (
          <TextLink
            data={{ role: 'PriceCheckerTextLink' }}
            href={localize({
              path: '/account/ad-price-lookup/preview',
              language: language as Language,
            })}
            target="_blank"
            onClick={handleAdPriceLookUpOnClick}
          >
            {children}
          </TextLink>
        ),
      }),
    },
  ];

  return (
    <Box data={{ role: 'Faq' }}>
      <Faqs faqs={faqs} heading={t('got a question')} />
    </Box>
  );
};

export { Faq };
